// src/constants.ts
var TOKEN = {
  EMPLOYEE: "E",
  CUSTOMER: "C",
  LOCK: "*",
  MEETING_TYPE: "M",
  OFFICE: "O",
  QUESTION: "Q",
  SUBJECT: "S",
  TIMETABLE: "T"
};
var DEFAULT_FLOW = [
  TOKEN.SUBJECT,
  TOKEN.MEETING_TYPE,
  TOKEN.OFFICE,
  TOKEN.TIMETABLE,
  TOKEN.QUESTION,
  TOKEN.CUSTOMER
].join("");
var SUPPORTED_LNGS = [
  "da",
  "de",
  "en",
  "en-GB",
  "en-US",
  "fr",
  "nb",
  "nl",
  "es",
  "el"
];
var FALLBACK_LNG = "en";

// src/PluginState.schema.ts
import { z } from "zod";

// src/utils.ts
var SPACE_OR_COMMA = /[\s,]+/;
function isSupportedLng(value) {
  return Boolean(value && SUPPORTED_LNGS.includes(value));
}
function getSupportedLng(value) {
  if (value === "cimode") {
    return "cimode";
  }
  if (isSupportedLng(value))
    return value;
  const locale = value?.split("-")[0];
  if (isSupportedLng(locale))
    return locale;
  return void 0;
}
function valueToArray(input, separator = SPACE_OR_COMMA) {
  if (typeof input === "string") {
    const trimmed = input.trim();
    if (trimmed === "")
      return void 0;
    return trimmed.split(separator).filter(Boolean);
  }
  return Array.isArray(input) ? input : void 0;
}
function validateGeoCoordinates(coordinates) {
  const [latitude, longitude] = coordinates?.split(",");
  if (typeof latitude !== "string" || typeof longitude !== "string") {
    return false;
  }
  return isValidLatitude(latitude) && isValidLongitude(longitude);
}
function isValidLongitude(lng) {
  const value = Number.parseInt(lng.trim());
  return !Number.isNaN(value) && Number.isFinite(value) && Math.abs(value) <= 180;
}
function isValidLatitude(lat) {
  const value = Number.parseInt(lat.trim());
  return !Number.isNaN(value) && Number.isFinite(value) && Math.abs(value) <= 90;
}

// src/PluginState.schema.ts
var MeetingTypeSchema = z.enum(["video", "phone", "on_location", "office"]);
var IntentSchema = z.enum([
  "schedule",
  "reschedule",
  "invite",
  "edit",
  "cancel",
  "complete"
]);
var LocationSourceSchema = z.enum(["config", "listing", "user"]);
var IdTypeSchema = z.enum(["id", "externalId"]);
var LeadSegmentTypeSchema = z.enum(["id", "code"]);
var LatLngSchema = z.string().transform((str) => str.replace(";", ",")).refine((value) => validateGeoCoordinates(value), {
  message: "location.geolocation must be a valid geolocation."
});
var BooleanFormSchema = z.union([
  z.string().transform((val) => val === "true" ? "on" : val === "false" ? null : val),
  z.boolean().transform((val) => val === true ? "on" : null)
]).nullish();
var StringFormSchema = z.string().nullish();
var StringSchema = z.string().optional();
var CoercedStringSchema = z.coerce.string().optional();
var ArraySchema = z.union([z.string(), z.string().array()]).nullish().transform((val) => valueToArray(val) ?? []);
var CoercedArraySchema = z.union([z.coerce.string(), z.coerce.string().array()]).nullish().transform((val) => valueToArray(val) ?? []);
var RecordSchema = z.record(z.any()).optional();
var QuestionSchema = z.record(z.union([z.string(), z.array(z.string())])).optional();
var TrueLiteralSchema = z.literal("true").optional();
var FalseLiteralSchema = z.literal("false").optional();
var CustomerSchema = z.object({
  company: StringFormSchema,
  customer_number: StringFormSchema,
  email: z.string().email().nullish(),
  external_id: StringFormSchema,
  first_name: StringFormSchema,
  id: z.coerce.string().nullish(),
  is_existing: BooleanFormSchema,
  language: StringFormSchema,
  last_name: StringFormSchema,
  location: z.object({
    geolocation: LatLngSchema.nullish(),
    city: StringFormSchema,
    country: StringFormSchema,
    formatted_address: StringFormSchema,
    postal_code: StringFormSchema,
    state: StringFormSchema,
    street_1: StringFormSchema
  }).nullish(),
  phone_number: StringFormSchema,
  // TODO: is validated against TIMEZONE_LIST in legacy plugin: See https://skedify.atlassian.net/browse/SKED-9964
  timezone: StringFormSchema
});
var PluginStateSchema = z.object({
  subjectId: StringSchema,
  officeId: StringSchema,
  employeeId: StringSchema,
  appointmentId: StringSchema,
  recaptcha: StringSchema,
  metadata: RecordSchema,
  possibilityId: StringSchema,
  meetingType: MeetingTypeSchema.optional(),
  timetable: z.object({
    start: StringSchema,
    end: StringSchema,
    /** used timezone when selecting a timeslot */
    timeZone: StringSchema,
    employeeIds: ArraySchema,
    autoSkip: FalseLiteralSchema
    // startDate: StringSchema,
  }).default({}),
  customer: CustomerSchema.optional(),
  questions: QuestionSchema,
  browser: z.object({ timeZone: StringSchema }).default({}),
  /**
   * Marking certain steps as (automatically) skipped or not.
   */
  skipped: z.object({
    subject: TrueLiteralSchema,
    office: TrueLiteralSchema,
    meetingType: TrueLiteralSchema,
    employee: TrueLiteralSchema
  }).default({}),
  errors: z.object({
    /**
     * Each step can have errors, in case of the timetable this currently only happens when
     * a slot is booked by another customer between selecting the slot, and confirming the appointment
     * (request).
     */
    timetable: TrueLiteralSchema
  }).default({}),
  /**
   * Warnings that should be logged to the console
   */
  warning: StringSchema,
  /** The selected geolocation of the user */
  location: z.object({
    source: LocationSourceSchema.default("user"),
    geolocation: LatLngSchema.optional(),
    formattedAddress: z.string().optional(),
    placeId: z.string().optional()
  }).optional(),
  config: z.object({
    /**
     * A sequence of characters that indicates the order of steps to be shown. The following characters are supported:
     * - `S`: Subject. The step that allows the customer to pick a subject.
     * - `O`: Office. The step that allows the customer to pick an office.
     * - `M`: Meeting type. The step that allows the customer to pick a meeting type.
     * - `E`: Employee. The step that allows the customer to pick an employee.
     * - `T`: Timetable. The step that allows the customer to suggest one or more time slots for their appointment.
     * - `Q`: Questions. The step that allows the customer to answer any addition questions specific to the chosen subject.
     * - `C`: Customer. The step that allows the customer to provide his/her contact information.
     * - `*`: [locked]. Indicate that the step is locked and can not be edited. This means that a value must be provided.
     *
     * Currently, the following constraints must always be met:
     * - The following characters must always be present: `T`, `Q`, `C`.
     * - No other characters than those listed above may be used.
     * - When `S` is not present, it is mandatory that an external mechanism will provide the `config.subject.id` + `config.subject.type` configuration option instead.
     * - When `O` is not present, it is mandatory that an external mechanism will provide the `config.office.id` + `config.office.type`configuration option instead.
     * - If `S` or `O` are present, they can never occur at a position after `T`.
     * - If `S` is present, it can never occur at a position after `Q`.
     *
     * By default the value `'SMOTQC` will be assumed. This will be the case if no value was provided or if the provided value fails these constraints.
     * @default "SMOTQC"
     */
    flow: z.string().default(DEFAULT_FLOW),
    intent: IntentSchema.default("schedule"),
    subjects: z.object({
      ids: CoercedArraySchema,
      type: IdTypeSchema.default("id")
    }).default({}),
    // TODO: this is not implemented yet on SRS/Old core, await API rework SRS
    subjectGroups: z.object({
      ids: CoercedArraySchema,
      type: IdTypeSchema.default("id")
    }).default({}),
    offices: z.object({
      ids: CoercedArraySchema,
      type: IdTypeSchema.default("id")
    }).default({}),
    listing: z.object({
      id: CoercedStringSchema,
      type: IdTypeSchema.default("id")
    }).default({}),
    employees: z.object({
      ids: CoercedArraySchema,
      type: IdTypeSchema.default("id")
    }).default({}),
    /**
     * Lead Segment. This allows a subject to be preselected matching the lead segment code/id.
     */
    leadSegment: z.object({
      id: CoercedStringSchema,
      type: LeadSegmentTypeSchema.default("code")
    }).default({}),
    oauth: z.object({
      /**
       * The bearer token used by the plugin. This token represents the contact.
       */
      // token: z.string().nullish(),
      /**
       * A resource_code of an existing appointment. This will allow you to complete or change an appointment. This parameter is mostly used by redirects from external sources (e.g. e-mails) and should usually not be provided manually.
       */
      resourceCode: StringSchema
    }).default({}),
    session: z.object({
      /**
       * Used to enable/disable tracking of events in the plugin for insights about conversion.
       * @default "default"
       */
      status: z.enum(["default", "disabled"]).default("default"),
      /**
       * Pass your own custom session ID.
       */
      id: StringSchema,
      /**
       * Pass source tags seperated with a semi-colon.
       */
      sourceTags: z.union([z.string(), z.string().array()]).nullish().transform((val) => valueToArray(val, ";"))
    }).default({}),
    customer: CustomerSchema.optional(),
    questions: QuestionSchema,
    /**
     * An RFC 5646-compatible language tag.
     *
     * This will cause the plugin to be displayed in the specified language, unless the language is unavailable.
     *
     * In that case, a best-effort fallback will be performed with increasingly more generic variants of the specified language or finally 'en' if no variants of the specified language are available.
     */
    language: StringSchema.transform((value) => getSupportedLng(value)),
    meetingTypes: ArraySchema,
    application: z.object({
      /**
       * Used to enable/disable the office search step.
       * @defualt "optional"
       * @type { "required" | "optional" }
       */
      officeSearch: z.enum(["required", "optional"]).default("optional"),
      /**
       * Used to set scroll top value when navigating steps
       * @default -90
       * @type { "disabled" | number }
       */
      scroll: z.union([z.enum(["disabled"]), z.coerce.number()]).default(-90),
      /**
       * Used to enable timezone selection when the meeting type is video or phone.
       * @default "enable_timezone_selection"
       * @type {"enable_timezone_selection" | "disable_timezone_selection"}
       */
      timezoneSelection: z.enum(["enable_timezone_selection", "disable_timezone_selection"]).default("enable_timezone_selection"),
      /**
       * Used to decide wether to show month of week timetable view
       * @experimental
       * @default "week"
       */
      timetableUI: z.enum(["week", "month"]).default("week")
    }).default({}),
    /**
     * This parameter is used for prefilling the location search step, so that offices are already scoped to this location.
     *
     * When location is passed, both geolocation & formatted_address are required.
     */
    location: z.object({
      source: z.enum(["config", "listing", "user"]).default("user"),
      geolocation: LatLngSchema.optional(),
      formattedAddress: z.string().optional(),
      placeId: z.string().optional()
    }).default({}),
    initialSearchAddress: z.string().optional(),
    /**
     * The country that should be used for searching locations. Should be a two letter ISO 3166-1 country code.
     *
     * You can provide a maximum of 5 comma-separated countries.
     */
    searchCountries: ArraySchema.refine((arg) => arg.length <= 5),
    sourceHref: z.string().optional()
  }).default({})
});
export {
  CustomerSchema,
  DEFAULT_FLOW,
  FALLBACK_LNG,
  IdTypeSchema,
  IntentSchema,
  LatLngSchema,
  LeadSegmentTypeSchema,
  LocationSourceSchema,
  MeetingTypeSchema,
  PluginStateSchema,
  SUPPORTED_LNGS,
  TOKEN,
  getSupportedLng,
  isSupportedLng
};
